import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw } from 'lucide-react';
import type { JsonReportContent } from '@/types';
import { generatePublishedUrl } from '@/utils/diffReportUtils';
import { supabase } from '@/integrations/supabase/client';

interface PublishConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPublishing: boolean;
  period: string;
  content: JsonReportContent | null;
}

export function PublishConfirmationDialog({
  open,
  onClose,
  onConfirm,
  isPublishing,
  period,
  content
}: PublishConfirmationDialogProps) {
  if (!content) return null;

  const publishedPath = generatePublishedUrl(period);

  // Get the proper Supabase public URL
  const { data } = supabase.storage
    .from('reports')
    .getPublicUrl(publishedPath);

  const fullUrl = data?.publicUrl || publishedPath;

  const trendsCount = content.trends?.length || 0;
  const examplesCount = content.trends?.reduce((sum, t) => sum + (t.examples?.length || 0), 0) || 0;
  const companiesCount = new Set(
    content.trends?.flatMap(t => t.examples?.map(e => e.slug) || []) || []
  ).size;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Publish Report?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>This will make the report publicly accessible at:</p>

              <div className="p-3 bg-gray-100 rounded-md">
                <code className="text-sm break-all text-gray-800">
                  {fullUrl}
                </code>
              </div>

              <div className="space-y-2">
                <p className="font-semibold text-gray-900">Report Summary:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>
                    <strong>{trendsCount}</strong> pricing trend{trendsCount === 1 ? '' : 's'}
                  </li>
                  <li>
                    <strong>{examplesCount}</strong> example{examplesCount === 1 ? '' : 's'} across <strong>{companiesCount}</strong> {companiesCount === 1 ? 'company' : 'companies'}
                  </li>
                  {content.metadata?.period_start && content.metadata?.period_end && (
                    <li>
                      Period: {formatDate(content.metadata.period_start)} → {formatDate(content.metadata.period_end)}
                    </li>
                  )}
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                The published version will remain stable even if you edit the draft. You can republish later to update the public version.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPublishing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isPublishing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isPublishing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Publishing...
              </>
            ) : (
              'Publish Report'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
